import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  MicrophoneIcon,
  StopIcon,
  PaperAirplaneIcon,
  SparklesIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';
import { aiApi } from '../../../services/api';

function ChatInterface({ selectedComponents, onComponentsChange, onViewPdf }) {
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [useAvailableDocuments, setUseAvailableDocuments] = useState(true);
  const [componentCatalog, setComponentCatalog] = useState([]);
  const [byCategory, setByCategory] = useState({});
  const [expandedChunks, setExpandedChunks] = useState({});
  const [expandedQueries, setExpandedQueries] = useState({});
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem('legal_kbChatCollapsed');
    return saved !== null ? saved === 'true' : true; // Default collapsed
  });
  const [width, setWidth] = useState(() => {
    const saved = localStorage.getItem('legal_kbChatWidth');
    return saved ? parseInt(saved, 10) : 420; // Default 420px
  });
  const [isResizing, setIsResizing] = useState(false);

  const messagesEndRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Save width and collapsed state to localStorage
  useEffect(() => {
    localStorage.setItem('legal_kbChatWidth', width.toString());
  }, [width]);

  useEffect(() => {
    localStorage.setItem('legal_kbChatCollapsed', isCollapsed.toString());
  }, [isCollapsed]);

  // Fetch component catalog on mount
  useEffect(() => {
    fetch('/api/component-catalog', { credentials: 'include' })
      .then(res => res.json())
      .then(data => {
        setComponentCatalog(data.components || []);
        setByCategory(data.by_category || {});
      })
      .catch(error => {
        console.error('Error fetching component catalog:', error);
      });
  }, []);

  // Handle resize
  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsResizing(true);
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isResizing) {
        const newWidth = window.innerWidth - e.clientX;
        const clampedWidth = Math.max(360, Math.min(800, newWidth));
        setWidth(clampedWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  const handleSendMessage = async (text) => {
    if (!text.trim()) return;

    const userMessage = { role: 'user', content: text };
    setMessages(prev => [...prev, userMessage]);
    setInputText('');
    setIsProcessing(true);
    setIsFilterOpen(false); // Close filter dropdown when sending message

    try {
      // Build conversation history
      const conversationHistory = [...messages, userMessage].map(msg => ({
        role: msg.role,
        content: msg.content
      }));

      // Call kb-backend streaming endpoint
      const response = await fetch('/api/chat/stream', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          message: text,
          conversation_history: conversationHistory,
          selected_components: selectedComponents.length > 0 ? selectedComponents : null,
          use_available_documents: useAvailableDocuments
        })
      });

      if (!response.ok) {
        throw new Error('Failed to stream response');
      }

      // Stream SSE response
      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      let currentMessage = {
        role: 'assistant',
        content: '',
        reformulatedQueries: null,
        retrievedChunks: null,
        sources: null
      };

      setMessages(prev => [...prev, currentMessage]);
      let messageIndex = messages.length + 1;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));

              if (data.type === 'text') {
                // Append text to current message
                setMessages(prev => {
                  const updated = [...prev];
                  updated[messageIndex] = {
                    ...updated[messageIndex],
                    content: updated[messageIndex].content + data.content
                  };
                  return updated;
                });
              } else if (data.type === 'reformulated_queries') {
                // Store reformulated queries
                setMessages(prev => {
                  const updated = [...prev];
                  updated[messageIndex] = {
                    ...updated[messageIndex],
                    reformulatedQueries: {
                      queries: data.queries,
                      reasoning: data.reasoning
                    }
                  };
                  return updated;
                });
              } else if (data.type === 'retrieved_chunks') {
                // Store retrieved chunks
                setMessages(prev => {
                  const updated = [...prev];
                  updated[messageIndex] = {
                    ...updated[messageIndex],
                    retrievedChunks: data.chunks
                  };
                  return updated;
                });
              } else if (data.type === 'sources') {
                // Store sources
                setMessages(prev => {
                  const updated = [...prev];
                  updated[messageIndex] = {
                    ...updated[messageIndex],
                    sources: data.sources
                  };
                  return updated;
                });
              } else if (data.type === 'result') {
                // Done
                break;
              } else if (data.type === 'error') {
                setMessages(prev => {
                  const updated = [...prev];
                  updated[messageIndex] = {
                    ...updated[messageIndex],
                    content: `Error: ${data.content}`,
                    error: true
                  };
                  return updated;
                });
              }
            } catch (e) {
              console.error('Error parsing SSE data:', e);
            }
          }
        }
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, I encountered an error processing your request. Please try again.',
        error: true
      }]);
    } finally {
      setIsProcessing(false);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        await processAudio(audioBlob);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
    } catch (error) {
      console.error('Error starting recording:', error);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const processAudio = async (audioBlob) => {
    setIsProcessing(true);
    try {
      const response = await aiApi.speechToText(audioBlob);
      const transcription = response.data.transcription;
      if (transcription) {
        // handleSendMessage will manage isProcessing from here
        handleSendMessage(transcription);
      } else {
        // No transcription - reset processing state
        setIsProcessing(false);
      }
    } catch (error) {
      console.error('Error processing audio:', error);
      setIsProcessing(false);
    }
  };

  const toggleComponentFilter = (componentId) => {
    if (selectedComponents.includes(componentId)) {
      onComponentsChange(selectedComponents.filter(id => id !== componentId));
    } else {
      onComponentsChange([...selectedComponents, componentId]);
    }
  };

  const clearAllFilters = () => {
    onComponentsChange([]);
    setIsFilterOpen(false);
    setSearchQuery('');
  };

  const getSelectedComponentNames = () => {
    return componentCatalog
      .filter(c => selectedComponents.includes(c.component_id))
      .map(c => c.name)
      .join(', ');
  };

  const toggleCategory = (category) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(category)) {
        newSet.delete(category);
      } else {
        newSet.add(category);
      }
      return newSet;
    });
  };

  const filterComponentsInCategory = (comps) => {
    if (!searchQuery.trim()) return comps;
    const query = searchQuery.toLowerCase();
    return comps.filter(c =>
      c.name.toLowerCase().includes(query) ||
      c.manufacturer?.toLowerCase().includes(query) ||
      c.model?.toLowerCase().includes(query) ||
      c.type?.toLowerCase().includes(query)
    );
  };

  const getFilteredByCategory = () => {
    const filtered = {};
    Object.entries(byCategory).forEach(([category, comps]) => {
      const filteredComps = filterComponentsInCategory(comps);
      if (filteredComps.length > 0) {
        filtered[category] = filteredComps;
      }
    });
    return filtered;
  };

  const toggleExpandChunks = (messageIndex) => {
    setExpandedChunks(prev => ({
      ...prev,
      [messageIndex]: !prev[messageIndex]
    }));
  };

  const toggleExpandQueries = (messageIndex) => {
    setExpandedQueries(prev => ({
      ...prev,
      [messageIndex]: !prev[messageIndex]
    }));
  };

  const handleChunkClick = (chunk) => {
    // Extract category from document name
    const category = chunk.document_name.includes('nfpa') || chunk.document_name.includes('api') || chunk.document_name.includes('fuel') || chunk.document_name.includes('ace') ? 'standards'
      : chunk.document_name.includes('cummins') || chunk.document_name.includes('cat') || chunk.document_name.includes('generac') ? 'oem_manuals'
      : 'installation_guides';

    onViewPdf({
      name: chunk.document_name,
      title: chunk.document_name.replace('.pdf', '').replace(/_/g, ' '),
      category: category
    }, chunk.page_number || 1);
  };

  // Collapsed button
  if (isCollapsed) {
    return (
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        className="fixed bottom-6 right-6 z-50"
      >
        <button
          onClick={() => setIsCollapsed(false)}
          className="w-14 h-14 rounded-full bg-gradient-to-br from-galaxy-500 to-galaxy-700 flex items-center justify-center shadow-lg hover:shadow-xl transition-all hover:scale-110"
          title="Open Knowledge Assistant"
        >
          <SparklesIcon className="w-6 h-6 text-white" />
        </button>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      style={{ width: `${width}px` }}
      className="bg-galaxy-900/60 border-l border-galaxy-800/50 flex flex-col h-full relative"
    >
      {/* Resize Handle */}
      <div
        onMouseDown={handleMouseDown}
        className={`absolute left-0 top-0 bottom-0 w-1 cursor-ew-resize hover:bg-galaxy-500/50 transition-colors ${
          isResizing ? 'bg-galaxy-500' : ''
        }`}
        style={{ touchAction: 'none' }}
      />

      {/* Header */}
      <div className="p-4 border-b border-galaxy-800/50">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-galaxy-500 to-galaxy-700 flex items-center justify-center">
            <SparklesIcon className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1">
            <h2 className="text-sm font-semibold text-white">Knowledge Assistant</h2>
          </div>
          <button
            onClick={() => setIsCollapsed(true)}
            className="p-1 text-galaxy-400 hover:text-white hover:bg-galaxy-800/50 rounded transition-colors"
            title="Minimize"
          >
            <ChevronDownIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Component Filter Dropdown */}
        <div className="relative">
          {selectedComponents.length === 0 ? (
            <button
              onClick={() => setIsFilterOpen(!isFilterOpen)}
              className="w-full text-left text-sm text-galaxy-400 hover:text-galaxy-300 flex items-center gap-2 px-2 py-1.5 rounded hover:bg-galaxy-800/30 transition-colors"
            >
              <span>🔧</span>
              <span className="flex-1">Filter by component (optional)</span>
              <ChevronDownIcon className={`w-4 h-4 transition-transform ${isFilterOpen ? 'rotate-180' : ''}`} />
            </button>
          ) : (
            <div className="flex items-center justify-between px-2 py-1.5">
              <button
                onClick={() => setIsFilterOpen(!isFilterOpen)}
                className="flex-1 text-left text-sm flex items-center gap-2 text-galaxy-300 hover:text-white"
              >
                <span>🔧</span>
                <span className="font-medium">
                  Filtered: {selectedComponents.length} component(s)
                </span>
                <ChevronDownIcon className={`w-4 h-4 transition-transform ${isFilterOpen ? 'rotate-180' : ''}`} />
              </button>
              <button
                onClick={clearAllFilters}
                className="text-xs text-red-400 hover:text-red-300 px-2"
              >
                Clear
              </button>
            </div>
          )}

          {selectedComponents.length > 0 && (
            <div className="mt-1 text-xs text-galaxy-500 px-2 truncate">
              {getSelectedComponentNames()}
            </div>
          )}

          {/* Dropdown Panel */}
          {isFilterOpen && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-galaxy-800/95 backdrop-blur-sm border border-galaxy-700/50 rounded-lg shadow-xl z-50 max-h-80 overflow-y-auto">
              <div className="p-3 space-y-3">
                {/* Use Available Documents checkbox */}
                <label className="flex items-center gap-2 p-2 hover:bg-galaxy-700/30 rounded cursor-pointer">
                  <input
                    type="checkbox"
                    checked={useAvailableDocuments}
                    onChange={(e) => setUseAvailableDocuments(e.target.checked)}
                    className="rounded border-galaxy-600 bg-galaxy-700 text-galaxy-500 focus:ring-galaxy-500"
                  />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-white">Use available documents</div>
                    <div className="text-xs text-galaxy-400">Always search with RAG</div>
                  </div>
                </label>

                {/* Search input */}
                {componentCatalog.length > 0 && (
                  <input
                    type="text"
                    placeholder="Search components..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full px-3 py-2 bg-galaxy-700/50 border border-galaxy-600/50 rounded text-sm text-white placeholder-galaxy-500 focus:outline-none focus:border-galaxy-500"
                  />
                )}

                {/* Component list by category */}
                {componentCatalog.length > 0 && (
                  <div>
                    <div className="text-xs text-galaxy-400 mb-2">
                      Select components to filter document search:
                    </div>
                    <div className="space-y-3">
                      {Object.keys(getFilteredByCategory()).length === 0 ? (
                        <div className="text-center py-4 text-galaxy-500 text-xs">
                          No components match "{searchQuery}"
                        </div>
                      ) : (
                        Object.entries(getFilteredByCategory()).map(([category, comps]) => {
                          const isExpanded = expandedCategories.has(category);
                          return (
                            <div key={category} className="space-y-1">
                              <button
                                onClick={() => toggleCategory(category)}
                                className="w-full text-left text-xs font-semibold text-galaxy-300 uppercase flex items-center gap-2 hover:text-white cursor-pointer"
                              >
                                <span>{isExpanded ? '▼' : '▶'}</span>
                                <span>{category}</span>
                                <span className="text-galaxy-500 font-normal">({comps.length})</span>
                              </button>
                              {isExpanded && (
                                <div className="ml-4 space-y-1">
                                  {comps.map(comp => (
                                    <label key={comp.component_id} className="flex items-start gap-2 p-2 hover:bg-galaxy-700/30 rounded cursor-pointer">
                                      <input
                                        type="checkbox"
                                        checked={selectedComponents.includes(comp.component_id)}
                                        onChange={() => toggleComponentFilter(comp.component_id)}
                                        className="mt-0.5 rounded border-galaxy-600 bg-galaxy-700 text-galaxy-500 focus:ring-galaxy-500"
                                      />
                                      <div className="flex-1">
                                        <div className="text-sm font-medium text-white">{comp.name}</div>
                                        <div className="text-xs text-galaxy-400">
                                          {comp.manufacturer} • {comp.model} • {comp.type}
                                        </div>
                                        <div className="text-xs text-galaxy-500">
                                          {comp.documents?.length || 0} document(s)
                                        </div>
                                      </div>
                                    </label>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 && (
          <div className="text-center py-8">
            <SparklesIcon className="w-12 h-12 text-galaxy-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">Knowledge Assistant</h3>
            <p className="text-sm text-galaxy-400 mb-6">
              Ask questions about engineering documents and standards
            </p>
          </div>
        )}

        {messages.map((message, index) => (
          <div key={index} className="space-y-2">
            {/* Main Message */}
            <div className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div
                className={`max-w-[85%] rounded-lg px-4 py-3 ${
                  message.role === 'user'
                    ? 'bg-galaxy-600 text-white'
                    : message.error
                    ? 'bg-red-900/30 text-red-300 border border-red-700/50'
                    : 'bg-galaxy-800/50 text-galaxy-100'
                }`}
              >
                <div className="text-sm prose prose-sm prose-invert prose-strong:font-bold prose-strong:text-white prose-em:italic prose-em:text-galaxy-200 prose-a:text-blue-400 prose-a:underline hover:prose-a:text-blue-300 prose-blockquote:border-l-4 prose-blockquote:border-galaxy-600 prose-blockquote:pl-4 prose-blockquote:italic prose-blockquote:text-galaxy-300 prose-hr:border-galaxy-700 prose-p:my-1 prose-headings:my-2 prose-ul:my-1 prose-ol:my-1 prose-li:my-0 prose-code:bg-galaxy-700/50 prose-code:px-1 prose-code:rounded prose-code:text-galaxy-200 prose-pre:bg-galaxy-800 prose-pre:p-2 max-w-none">
                  {message.role === 'assistant' ? (
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {message.content}
                    </ReactMarkdown>
                  ) : (
                    <span className="whitespace-pre-wrap">{message.content}</span>
                  )}
                </div>
              </div>
            </div>

            {/* Retrieved Chunks (for assistant messages) */}
            {message.retrievedChunks && message.retrievedChunks.length > 0 && (
              <div className="ml-4">
                <button
                  onClick={() => toggleExpandChunks(index)}
                  className="flex items-center gap-2 text-xs text-galaxy-400 hover:text-galaxy-300 transition-colors"
                >
                  {expandedChunks[index] ? <ChevronUpIcon className="w-4 h-4" /> : <ChevronDownIcon className="w-4 h-4" />}
                  <span>🔍 Retrieved {message.retrievedChunks.length} document chunk(s)</span>
                </button>

                {expandedChunks[index] && (
                  <div className="mt-2 space-y-2">
                    {message.retrievedChunks.map((chunk, chunkIndex) => (
                      <div
                        key={chunkIndex}
                        onClick={() => handleChunkClick(chunk)}
                        className="bg-galaxy-900/50 border border-galaxy-700/30 rounded-lg p-3 text-xs cursor-pointer hover:border-galaxy-600/50 transition-colors"
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <DocumentTextIcon className="w-4 h-4 text-galaxy-500" />
                          <span className="font-medium text-galaxy-300">{chunk.document_name}</span>
                          <span className="text-galaxy-500">Page {chunk.page_number}</span>
                          <span className="ml-auto text-galaxy-500">{Math.round(chunk.similarity_score * 100)}% match</span>
                        </div>
                        <p className="text-galaxy-400 line-clamp-2">{chunk.text.substring(0, 150)}...</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Reformulated Queries */}
            {message.reformulatedQueries && (
              <div className="ml-4">
                <button
                  onClick={() => toggleExpandQueries(index)}
                  className="flex items-center gap-2 text-xs text-galaxy-400 hover:text-galaxy-300 transition-colors"
                >
                  {expandedQueries[index] ? <ChevronUpIcon className="w-4 h-4" /> : <ChevronDownIcon className="w-4 h-4" />}
                  <span>🔍 Search queries used ({message.reformulatedQueries.queries.length})</span>
                </button>

                {expandedQueries[index] && (
                  <div className="mt-2 space-y-1">
                    {message.reformulatedQueries.queries.map((query, qIndex) => (
                      <div key={qIndex} className="bg-galaxy-900/30 border border-galaxy-700/20 rounded px-2 py-1 text-xs text-galaxy-400">
                        {query}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}

        {isProcessing && (
          <div className="flex justify-start">
            <div className="bg-galaxy-800/50 rounded-lg px-4 py-3">
              <div className="flex items-center gap-2">
                <div className="flex gap-1">
                  <div className="w-2 h-2 bg-galaxy-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <div className="w-2 h-2 bg-galaxy-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <div className="w-2 h-2 bg-galaxy-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
                <span className="text-sm text-galaxy-400">Processing...</span>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="p-4 border-t border-galaxy-800/50">
        {/* Recording Indicator */}
        {isRecording && (
          <div className="mb-3 flex items-center justify-center gap-2 text-status-npt">
            <div className="flex gap-1">
              {[...Array(5)].map((_, i) => (
                <div
                  key={i}
                  className="waveform-bar w-1 h-4 bg-status-npt rounded-full"
                  style={{ animationDelay: `${i * 0.1}s` }}
                />
              ))}
            </div>
            <span className="text-sm">Listening...</span>
          </div>
        )}

        {/* Processing Indicator - shows during speech-to-text and message processing */}
        {isProcessing && !isRecording && (
          <div className="mb-3 flex items-center justify-center gap-2 text-galaxy-400">
            <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            <span className="text-sm">Processing...</span>
          </div>
        )}

        <div className="flex items-center gap-2">
          {/* Voice Input Button */}
          <button
            onMouseDown={startRecording}
            onMouseUp={stopRecording}
            onMouseLeave={stopRecording}
            className={`p-3 rounded-full transition-colors ${
              isRecording
                ? 'bg-status-npt text-white'
                : 'bg-galaxy-800 text-galaxy-400 hover:text-white hover:bg-galaxy-700'
            }`}
          >
            {isRecording ? (
              <StopIcon className="w-5 h-5" />
            ) : (
              <MicrophoneIcon className="w-5 h-5" />
            )}
          </button>

          {/* Text Input */}
          <input
            type="text"
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSendMessage(inputText)}
            placeholder="Ask about documents..."
            className="flex-1 bg-galaxy-800/50 border border-galaxy-700/50 rounded-lg px-4 py-2.5 text-sm text-white placeholder-galaxy-500 focus:outline-none focus:border-galaxy-500"
          />

          {/* Send Button */}
          <button
            onClick={() => handleSendMessage(inputText)}
            disabled={!inputText.trim() || isProcessing}
            className="p-3 bg-galaxy-600 text-white rounded-full hover:bg-galaxy-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isProcessing ? (
              <svg className="w-5 h-5 animate-spin" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            ) : (
              <PaperAirplaneIcon className="w-5 h-5" />
            )}
          </button>
        </div>
      </div>
    </motion.div>
  );
}

export default ChatInterface;
